import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Building2, ShieldCheck, LogIn, Megaphone, Sparkles, Tag, CheckCircle2,
  DollarSign, UserCog, Mail, AlertTriangle, Copy, Globe, Users, Clock, Zap, Hand, Star, RefreshCw, Archive, Trash2, Pin,
} from "lucide-react";
import { sr, actAs } from "../../lib/sr";
import { auth } from "../../lib/api";
import HealthBadge from "./HealthBadge";
import IndustryIntake, { PlaybookCard } from "../../components/smartreach/IndustryIntake";
import CompetitorCompare from "../../components/smartreach/CompetitorCompare";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const fmt = (ts) => { try { return new Date(ts).toLocaleDateString(); } catch { return ts; } };

const STATUS_BADGE = {
  LIVE: "sr-badge-green", ARCHIVED: "sr-badge-gray", DRAFT: "sr-badge-gray",
  AWAITING_APPROVAL: "sr-badge-amber", APPROVED: "sr-badge-green", SUBMITTED: "sr-badge",
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="sr-card" style={{ flex: 1, minWidth: 120 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon size={16} style={{ color: "var(--blue)" }} />
      <span style={{ fontSize: 22, fontWeight: 800, color: "var(--navy)" }}>{value}</span>
    </div>
    <div className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>{label}</div>
  </div>
);

function Section({ title, count, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <h3 style={{ fontSize: 16, color: "var(--navy)" }}>{title}</h3>
        <span className="sr-badge sr-badge-gray">{count}</span>
      </div>
      {children}
    </div>
  );
}

const HISTORY_META = {
  owner: { label: "Owner", icon: UserCog },
  manager: { label: "Assigned manager", icon: Users },
  execution_mode: { label: "Execution mode", icon: Zap },
};
const modeLabel = (m) => (m === "ASSISTED" ? "Assisted (team-run)" : "Self-serve");

export default function AdminClientDetail() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const isSuperAdmin = (auth.getUser() || {}).role === "super_admin";
  const [fee, setFee] = useState(null);
  const [feeBusy, setFeeBusy] = useState(false);
  const [feePreview, setFeePreview] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [dangerBusy, setDangerBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [compareKey, setCompareKey] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const archiveClient = async () => {
    const t = data.tenant;
    if (!window.confirm(`Archive ${t.name}? It will be hidden from the active clients list but its data is kept — you can restore it later.`)) return;
    setDangerBusy(true);
    try { await sr.adminArchiveClient(tenantId); toast.success(`${t.name} archived`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't archive"); } finally { setDangerBusy(false); }
  };
  const restoreClient = async () => {
    setDangerBusy(true);
    try { await sr.adminRestoreClient(tenantId); toast.success("Client restored to active list"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't restore"); } finally { setDangerBusy(false); }
  };
  const deleteClient = async () => {
    setDangerBusy(true);
    try { const r = await sr.adminDeleteClient(tenantId, confirmName); toast.success(r.export_emailed ? "Client deleted — a recovery export was emailed to you" : "Client permanently deleted"); nav("/app/admin/clients"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete client"); setDangerBusy(false); }
  };
  const [newEmail, setNewEmail] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [savingMgr, setSavingMgr] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [teamAssigns, setTeamAssigns] = useState([]);
  const [assignUser, setAssignUser] = useState("");
  const [assignRole, setAssignRole] = useState("");
  const [assigningRole, setAssigningRole] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetKey, setPresetKey] = useState("");
  const [presetMembers, setPresetMembers] = useState({});
  const [applyingPreset, setApplyingPreset] = useState(false);
  const [biz, setBiz] = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);
  const [schema, setSchema] = useState(null);
  const [ind, setInd] = useState("");
  const [intakeDraft, setIntakeDraft] = useState({});
  const [sellType, setSellType] = useState("physical");
  const [savingInd, setSavingInd] = useState(false);
  const saveIndustry = async () => {
    setSavingInd(true);
    try {
      await sr.adminUpdateClientProfile(tenantId, { industry: ind, intake: intakeDraft, operating_model: sellType });
      toast.success("Industry & intake saved — the AI will use this");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingInd(false); }
  };
  const [refreshingBiz, setRefreshingBiz] = useState(false);
  const refreshFromGoogle = async () => {
    setRefreshingBiz(true);
    try {
      const r = await sr.adminRefreshBusiness(tenantId);
      if (r.ok) { toast.success(`Refreshed from Google${r.updated?.length ? ` · ${r.updated.join(", ")}` : ""}`); load(); }
      else if (String(r.error || "").startsWith("places_error")) toast.error("Google Places is temporarily unavailable (API key/billing). Try again later.");
      else toast.error(r.error || "Couldn't refresh from Google");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't refresh"); } finally { setRefreshingBiz(false); }
  };

  const load = () => sr.adminClientDetail(tenantId).then(setData).catch(() => setData({ error: true }));
  const loadTeam = () => sr.rbacAssignments({ scope_type: "client", scope_id: tenantId })
    .then((r) => setTeamAssigns(r.assignments || [])).catch(() => {});
  useEffect(() => {
    load(); loadTeam();
    sr.adminStaff().then((r) => setStaff(r.staff || [])).catch(() => {});
    sr.rbacRoles().then((r) => setRoleTemplates(r.templates || [])).catch(() => {});
    sr.rbacPresets().then((r) => setPresets(r.presets || [])).catch(() => {});
    sr.onboardingSchema().then(setSchema).catch(() => {});
  }, [tenantId]);
  useEffect(() => {
    if (!data?.tenant) return;
    setInd(data.profile?.industry || data.tenant?.industry || "");
    setIntakeDraft(data.profile?.intake || {});
    setSellType(data.tenant?.operating_model || "physical");
  }, [data?.tenant?.id]);

  const manage = async (t) => {
    actAs.set(t.id, t.name);
    try { await sr.adminActAs(t.id); } catch { /* best-effort */ }
    toast.success(`Now managing ${t.name}`);
    window.location.assign("/app");
  };

  const assignManager = async (managerId) => {
    setSavingMgr(true);
    try { await sr.adminAssignManager(tenantId, managerId); toast.success(managerId ? "Manager assigned" : "Manager cleared"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign manager"); }
    finally { setSavingMgr(false); }
  };

  const [capInput, setCapInput] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  useEffect(() => { if (data?.tenant) setCapInput(data.tenant.spend_cap_daily ? String(data.tenant.spend_cap_daily) : ""); }, [data?.tenant?.spend_cap_daily]);
  const saveCap = async () => {
    setSavingCap(true);
    const cap = parseFloat(capInput || "0") || 0;
    try { const r = await sr.adminSetClientSpendCap(tenantId, cap); toast.success(r.daily_cap ? `Daily cap set to $${r.daily_cap}` : "Daily cap cleared"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save cap"); }
    finally { setSavingCap(false); }
  };

  useEffect(() => {
    const o = data?.tenant?.platform_fee_override;
    setFee(o ? { ...o } : { enabled: false, mode: "pct", pct: 0, flat_amount: 0, note: "" });
  }, [data?.tenant?.id, data?.tenant?.platform_fee_override]);
  const saveFee = async () => {
    setFeeBusy(true);
    try {
      const body = {
        enabled: !!fee.enabled, mode: fee.mode === "flat" ? "flat" : "pct",
        pct: Math.max(Number(fee.pct) || 0, 0), flat_amount: Math.max(Number(fee.flat_amount) || 0, 0),
        note: fee.note || "",
      };
      const r = await sr.setTenantPlatformFee(tenantId, body);
      setFee({ ...r.platform_fee_override });
      toast.success(body.enabled ? "Platform fee saved — folded into this client's markup" : "Platform fee turned off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save platform fee"); }
    finally { setFeeBusy(false); }
  };

  useEffect(() => {
    if (!isSuperAdmin || !fee) { setFeePreview(null); return; }
    const timer = setTimeout(() => {
      sr.tenantPlatformFeePreview(tenantId, {
        enabled: !!fee.enabled, mode: fee.mode === "flat" ? "flat" : "pct",
        pct: Math.max(Number(fee.pct) || 0, 0), flat_amount: Math.max(Number(fee.flat_amount) || 0, 0),
      }).then(setFeePreview).catch(() => setFeePreview(null));
    }, 350);
    return () => clearTimeout(timer);
  }, [isSuperAdmin, tenantId, fee?.enabled, fee?.mode, fee?.pct, fee?.flat_amount]); // eslint-disable-line

  const setMode = async (mode) => {
    setSavingMode(true);
    try {
      const r = await sr.adminSetExecutionMode(tenantId, mode);
      toast.success(`Execution mode: ${modeLabel(mode)}`);
      if (r.needs_manager) toast.warning("Assign a campaign manager to run this account.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change mode"); }
    finally { setSavingMode(false); }
  };

  const reassign = async () => {
    if (!newEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!window.confirm(`Reassign ownership of ${data.tenant.name} to ${newEmail}? The current owner will lose access to this workspace.`)) return;
    setBusy(true);
    try {
      const r = await sr.adminReassignOwner(tenantId, { owner_email: newEmail, send_invite: sendInvite });
      setResult(r.owner);
      toast.success("Owner reassigned");
      setReassigning(false); setNewEmail(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign owner"); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const grantRole = async () => {
    if (!assignUser || !assignRole) { toast.error("Pick a team member and a role"); return; }
    setAssigningRole(true);
    try {
      await sr.rbacAssign({ user_id: assignUser, role: assignRole, scope_type: "client", scope_id: tenantId });
      toast.success("Role assigned for this client");
      setAssignUser(""); setAssignRole(""); loadTeam();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign role"); }
    finally { setAssigningRole(false); }
  };
  const revokeRole = async (id) => {
    if (!window.confirm("Revoke this scoped role assignment?")) return;
    try { await sr.rbacRevoke(id); toast.success("Role revoked"); loadTeam(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };
  const applyPreset = async () => {
    const preset = presets.find((p) => p.key === presetKey);
    if (!preset) return;
    const members = preset.roles.map((r) => ({ role: r.slug, user_id: presetMembers[r.slug] })).filter((m) => m.user_id);
    if (!members.length) { toast.error("Assign at least one person to a role"); return; }
    setApplyingPreset(true);
    try {
      const r = await sr.rbacApplyPreset({ scope_type: "client", scope_id: tenantId, members });
      toast.success(`Team applied — ${r.created} added${r.skipped ? `, ${r.skipped} already set` : ""}`);
      setPresetKey(""); setPresetMembers({}); loadTeam();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply team"); }
    finally { setApplyingPreset(false); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load this client. Admin access is required.</p></div>;

  const b = biz || { phone: data.profile?.phone || "", address: data.profile?.address || "", competitors: data.profile?.competitors || [] };
  const saveBiz = async (patch) => {
    const next = { ...b, ...patch };
    setSavingBiz(true);
    try { await sr.adminUpdateClientProfile(tenantId, { phone: next.phone, address: next.address, competitors: next.competitors }); setBiz(next); toast.success("Business details saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingBiz(false); }
  };
  const removeCompetitor = (pid) => saveBiz({ competitors: (b.competitors || []).filter((c) => c.place_id !== pid) });
  const pinTarget = async (c) => {
    if (!c.place_id) { toast.error("This competitor has no Google place id to track"); return; }
    setPinBusy(true);
    try {
      await sr.pinCompetitor({ place_id: c.place_id, name: c.name, address: c.address }, tenantId);
      setCompareKey((k) => k + 1);
      toast.success(`Pinned ${c.name} to compare`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not pin"); } finally { setPinBusy(false); }
  };

  const { tenant, owner, manager, profile, campaigns, growth_plans, offers, stats, history, needs_manager, health } = data;
  const mode = tenant.execution_mode || "SELF_SERVE";
  const people = [...staff];
  if (owner && !people.some((p) => p.id === owner.id)) people.push({ id: owner.id, name: owner.name, email: owner.email });
  const assignableRoles = roleTemplates.filter((t) => t.scope !== "platform");

  return (
    <div data-testid="admin-client-detail-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/admin/clients")} data-testid="back-to-clients" style={{ marginBottom: 12 }}>
        <ArrowLeft size={15} /> All clients
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Building2 size={20} style={{ color: "var(--navy)" }} />
            <h1 style={{ fontSize: 28 }}>{tenant.name}</h1>
            {tenant.is_demo && <span className="sr-badge sr-badge-amber">Demo</span>}
            <HealthBadge health={health} />
          </div>
          <p className="sr-muted" style={{ marginTop: 6, fontSize: 13 }}>
            {owner ? `Owner: ${owner.name || ""} · ${owner.email}` : tenant.client_invited_email ? `Invited: ${tenant.client_invited_email}` : "No owner assigned"}
            {profile?.website ? <> · <Globe size={11} /> {profile.website}</> : ""}
            {profile?.category ? ` · ${profile.category}` : ""} · created {fmt(tenant.created_at)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setReassigning(!reassigning); setResult(null); }} data-testid="reassign-owner-btn">
            <UserCog size={14} /> Reassign owner
          </button>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => manage(tenant)} data-testid="detail-manage-btn">
            <LogIn size={14} /> Manage / Act as
          </button>
        </div>
      </div>

      <div className="sr-card" style={{ marginTop: 14 }} data-testid="client-biz-details">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div className="sr-eyebrow">Business details</div>
          {profile?.place_id && (
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={refreshFromGoogle} disabled={refreshingBiz} data-testid="refresh-google-btn">
              {refreshingBiz ? <Loader2 size={13} className="sr-spin" /> : <RefreshCw size={13} />} Refresh from Google
            </button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Phone</label>
            <input className="sr-input" defaultValue={b.phone} onBlur={(e) => e.target.value !== b.phone && saveBiz({ phone: e.target.value })} data-testid="detail-phone" placeholder="(555) 123-4567" /></div>
          <div className="sr-field"><label className="sr-label">Address</label>
            <input className="sr-input" defaultValue={b.address} onBlur={(e) => e.target.value !== b.address && saveBiz({ address: e.target.value })} data-testid="detail-address" placeholder="Street, City, State" /></div>
        </div>
        {(profile?.rating || (profile?.hours && profile.hours.length) || profile?.summary || profile?.maps_uri) && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }} data-testid="detail-google-info">
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 13, color: "var(--navy)" }}>
              {profile.rating != null && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="detail-rating">
                  <Star size={14} style={{ color: "#E0A94A", fill: "#E0A94A" }} /> <b>{profile.rating}</b>
                  {profile.rating_count ? <span className="sr-muted">({profile.rating_count})</span> : null}
                </span>
              )}
              {profile.hours && profile.hours.length > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="detail-hours">
                  <Clock size={14} style={{ color: "var(--blue)" }} /> {profile.hours.length} days of hours
                </span>
              )}
              {profile.maps_uri && <a href={profile.maps_uri} target="_blank" rel="noreferrer" style={{ fontSize: 12 }} data-testid="detail-maps-link">Google Maps →</a>}
            </div>
            {profile.summary && <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }} data-testid="detail-summary">{profile.summary}</p>}
            {profile.hours && profile.hours.length > 0 && (
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.7 }}>
                {profile.hours.map((h, i) => <div key={i}>{h}</div>)}
              </div>
            )}
          </div>
        )}
        {(b.competitors || []).length > 0 && (
          <div style={{ marginTop: 8 }} data-testid="detail-competitors">
            <label className="sr-label">Competitor targets ({b.competitors.length})</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
              {b.competitors.map((c) => (
                <span key={c.place_id || c.name} className="sr-badge" style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid={`detail-comp-${c.place_id || c.name}`}>
                  {c.name}
                  {c.place_id && <span onClick={() => pinTarget(c)} style={{ cursor: pinBusy ? "wait" : "pointer", display: "inline-flex", alignItems: "center" }} title="Pin to compare" data-testid={`detail-comp-pin-${c.place_id}`}><Pin size={12} /></span>}
                  <span onClick={() => removeCompetitor(c.place_id)} style={{ cursor: "pointer", fontWeight: 700 }} data-testid={`detail-comp-remove-${c.place_id || c.name}`}>×</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <CompetitorCompare key={compareKey} tenantId={tenantId} title="Competitor watch — rating &amp; review trend" />

      {schema && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="client-industry-intake">
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Industry &amp; campaign intake</div>
          <p className="sr-muted" style={{ fontSize: 12, marginBottom: 10 }}>Update how this client sells, its industry, and the intake answers the AI uses to build the right ads.</p>
          <IndustryIntake schema={schema} idPrefix="cd" sections={["sellType", "industry", "questions"]}
            sellType={sellType} onSellType={setSellType}
            industry={ind} onIndustry={setInd}
            intake={intakeDraft} onIntake={setIntakeDraft} />
          {ind && schema.playbooks?.[ind] && (<div style={{ marginTop: 12 }}><PlaybookCard playbook={schema.playbooks[ind]} /></div>)}
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={saveIndustry} disabled={savingInd} data-testid="cd-save-industry">
            {savingInd ? <Loader2 size={14} className="sr-spin" /> : <CheckCircle2 size={14} />} Save industry &amp; intake
          </button>
        </div>
      )}

      {reassigning && (
        <div className="sr-card" style={{ marginTop: 14, border: "1px solid #E3C878", background: "#FCF8EE" }} data-testid="reassign-form">
          <p style={{ fontSize: 14, fontWeight: 700, color: "#7A5B12", display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={15} /> Reassign ownership
          </p>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>
            The current owner ({owner?.email || "none"}) will be detached from this workspace and the new owner attached. A login is created if the email is new.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <input className="sr-input" placeholder="new-owner@business.com" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} data-testid="reassign-email" style={{ minWidth: 240 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--navy)", cursor: "pointer" }}>
              <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="reassign-invite-toggle" />
              <Mail size={13} /> Email invite
            </label>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={reassign} disabled={busy} data-testid="reassign-confirm">
              {busy ? <Loader2 size={14} className="sr-spin" /> : <><CheckCircle2 size={14} /> Confirm reassignment</>}
            </button>
          </div>
          {result && (
            <div className="sr-card" style={{ marginTop: 12, background: "#F1FBF4", border: "1px solid #B6E3C6" }} data-testid="reassign-result">
              <p style={{ fontSize: 13, color: "#067647", fontWeight: 700 }}><CheckCircle2 size={13} /> Now owned by {result.email}{result.existing ? " (existing account)" : ""}</p>
              {result.previous_owner && <p className="sr-muted" style={{ fontSize: 12 }}>Detached previous owner: {result.previous_owner}</p>}
              {result.temp_password && (
                <p className="sr-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                  Temp password: <b>{result.temp_password}</b>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copy(result.temp_password)} data-testid="copy-reassign-pw"><Copy size={12} /></button>
                </p>
              )}
              <p className="sr-muted" style={{ fontSize: 12 }}>{result.emailed ? "Invite emailed ✓" : "Share sign-in details securely."}</p>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 18 }} data-testid="team-execution-card">
        <div className="sr-card" style={{ flex: 1, minWidth: 280 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Execution</div>
          <div style={{ display: "flex", gap: 8 }} data-testid="execution-mode-toggle">
            {["ASSISTED", "SELF_SERVE"].map((m) => (
              <button key={m} onClick={() => setMode(m)} disabled={savingMode || mode === m}
                data-testid={`mode-${m.toLowerCase()}`}
                style={{ flex: 1, border: "1px solid " + (mode === m ? "var(--blue)" : "var(--border)"),
                background: mode === m ? "var(--blue)" : "#fff", color: mode === m ? "var(--navy)" : "var(--navy)",
                  borderRadius: 10, padding: "10px 8px", cursor: mode === m ? "default" : "pointer",
                  fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {m === "ASSISTED" ? <Zap size={14} /> : <Hand size={14} />} {modeLabel(m)}
              </button>
            ))}
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
            {mode === "ASSISTED" ? "Your team runs this client's campaigns for them." : "The client runs their own campaigns."}
          </p>
        </div>

        <div className="sr-card" style={{ flex: 1, minWidth: 280 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Assigned manager</div>
          <select className="sr-select" value={manager?.id || ""} disabled={savingMgr}
            onChange={(e) => assignManager(e.target.value)} data-testid="assign-manager-select">
            <option value="">— Unassigned —</option>
            {staff.map((s) => {
              const label = `${s.name || s.email} (${(s.role || "").replace(/_/g, " ")})`;
              return <option key={s.id} value={s.id}>{label}</option>;
            })}
          </select>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
            {manager ? <><Users size={11} /> {manager.email} runs this account.</> : "No internal manager assigned."}
          </p>
        </div>

        <div className="sr-card" style={{ flex: 1, minWidth: 280 }} data-testid="client-spend-cap-card">
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Daily spend cap</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span className="sr-muted" style={{ fontSize: 14 }}>$</span>
            <input className="sr-input" type="number" min="0" placeholder="No cap" value={capInput}
              onChange={(e) => setCapInput(e.target.value)} data-testid="client-spend-cap-input" style={{ maxWidth: 130 }} />
            <span className="sr-muted" style={{ fontSize: 13 }}>/day</span>
            <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={saveCap} disabled={savingCap} data-testid="client-spend-cap-save">Save</button>
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
            Auto-pauses this client's campaigns if a day's spend goes over. Overrides the global cap; leave blank to use the global rule.
          </p>
        </div>
      </div>

      {needs_manager && (
        <div className="sr-card" style={{ marginTop: 12, border: "1px solid #E3C878", background: "#FCF8EE", display: "flex", alignItems: "center", gap: 8 }} data-testid="needs-manager-banner">
          <AlertTriangle size={16} style={{ color: "#9A7412" }} />
          <span style={{ fontSize: 13, color: "#7A5B12", fontWeight: 600 }}>
            This client chose Assisted execution but has no campaign manager assigned. Pick one above so someone runs their campaigns.
          </span>
        </div>
      )}

      {isSuperAdmin && fee && (
        <div className="sr-card" style={{ marginTop: 18 }} data-testid="platform-fee-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <DollarSign size={16} style={{ color: "var(--blue)" }} />
            <h3 style={{ fontSize: 16, color: "var(--navy)", margin: 0 }}>Platform fee (Super Admin)</h3>
          </div>
          <p className="sr-muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Off by default. When on, this fee is folded into this client's media markup as overhead — it never shows as a separate line on their plan.
          </p>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
            <input type="checkbox" checked={!!fee.enabled} onChange={(e) => setFee({ ...fee, enabled: e.target.checked })} data-testid="platform-fee-enabled" />
            Charge a platform fee for this client
          </label>
          {fee.enabled && (
            <div style={{ marginTop: 14, display: "grid", gap: 12 }} data-testid="platform-fee-fields">
              <div style={{ display: "flex", gap: 8 }} data-testid="platform-fee-mode">
                <button className={`sr-btn sr-btn-sm ${fee.mode !== "flat" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setFee({ ...fee, mode: "pct" })} data-testid="platform-fee-mode-pct">Percentage</button>
                <button className={`sr-btn sr-btn-sm ${fee.mode === "flat" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setFee({ ...fee, mode: "flat" })} data-testid="platform-fee-mode-flat">Flat monthly</button>
              </div>
              {fee.mode === "flat" ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="sr-muted" style={{ fontSize: 14 }}>$</span>
                  <input className="sr-input" type="number" min="0" step="10" value={fee.flat_amount ?? 0}
                    onChange={(e) => setFee({ ...fee, flat_amount: e.target.value })} data-testid="platform-fee-flat-input" style={{ maxWidth: 150 }} />
                  <span className="sr-muted" style={{ fontSize: 13 }}>/mo, spread across media as markup</span>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input className="sr-input" type="number" min="0" step="0.5" value={fee.pct ?? 0}
                    onChange={(e) => setFee({ ...fee, pct: e.target.value })} data-testid="platform-fee-pct-input" style={{ maxWidth: 120 }} />
                  <span className="sr-muted" style={{ fontSize: 13 }}>% extra markup on media</span>
                </div>
              )}
              <input className="sr-input" maxLength={200} value={fee.note || ""} placeholder="Internal note (optional)"
                onChange={(e) => setFee({ ...fee, note: e.target.value })} data-testid="platform-fee-note" />
            </div>
          )}
          {feePreview && (
            <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="platform-fee-preview">
              <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
                Effective markup on a ${feePreview.budget.toLocaleString()}/mo plan
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span data-testid="fee-preview-before" style={{ fontSize: 20, fontWeight: 700, color: "var(--muted)" }}>{feePreview.before.effective_markup_pct}%</span>
                <span style={{ color: "var(--muted)" }}>→</span>
                <span data-testid="fee-preview-after" style={{ fontSize: 20, fontWeight: 800, color: feePreview.overhead_pct > 0 ? "var(--blue)" : "var(--muted)" }}>{feePreview.after.effective_markup_pct}%</span>
                {feePreview.overhead_pct > 0 && (
                  <span className="sr-badge" style={{ background: "#EEF4FF", color: "var(--blue)" }}>+{feePreview.overhead_pct} pts</span>
                )}
              </div>
              {feePreview.overhead_pct > 0 ? (
                <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>
                  Media buying power ${feePreview.before.provider_media.toLocaleString()} → <strong style={{ color: "var(--navy)" }}>${feePreview.after.provider_media.toLocaleString()}</strong> — about <strong>${Math.abs(feePreview.media_delta).toLocaleString()}</strong> absorbed as markup. The client never sees a separate fee line.
                </p>
              ) : (
                <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>No fee applied — the client pays media plus standard markup only.</p>
              )}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveFee} disabled={feeBusy} data-testid="platform-fee-save">
              {feeBusy ? <Loader2 size={14} className="sr-spin" /> : "Save platform fee"}
            </button>
          </div>
        </div>
      )}

      <div className="sr-card" style={{ marginTop: 18 }} data-testid="team-roles-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <ShieldCheck size={16} style={{ color: "var(--blue)" }} />
          <h3 style={{ fontSize: 16, color: "var(--navy)" }}>Team &amp; roles</h3>
          <span className="sr-badge sr-badge-gray">{teamAssigns.length}</span>
        </div>
        <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Scoped access for this client — assignments here grant capabilities only within {tenant.name}.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <select className="sr-select" value={assignUser} onChange={(e) => setAssignUser(e.target.value)}
            data-testid="assign-role-user" style={{ minWidth: 200 }}>
            <option value="">— Team member —</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
          </select>
          <select className="sr-select" value={assignRole} onChange={(e) => setAssignRole(e.target.value)}
            data-testid="assign-role-role" style={{ minWidth: 200 }}>
            <option value="">— Role —</option>
            {assignableRoles.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={grantRole} disabled={assigningRole} data-testid="assign-role-btn">
            {assigningRole ? <Loader2 size={14} className="sr-spin" /> : <><ShieldCheck size={14} /> Assign role</>}
          </button>
          <Link to="/app/admin/roles" style={{ fontSize: 12 }} data-testid="view-all-roles-link">View all roles &amp; permissions →</Link>
        </div>

        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginBottom: 12 }} data-testid="preset-section">
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}><Users size={13} style={{ verticalAlign: "-2px", color: "var(--blue)" }} /> Apply a team preset</span>
            <select className="sr-select" value={presetKey} onChange={(e) => { setPresetKey(e.target.value); setPresetMembers({}); }}
              data-testid="preset-select" style={{ minWidth: 180 }}>
              <option value="">— Choose a preset —</option>
              {presets.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </div>
          {presetKey && (() => {
            const preset = presets.find((p) => p.key === presetKey);
            if (!preset) return null;
            return (
              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                <p className="sr-muted" style={{ fontSize: 12 }}>{preset.description} Assign a person to each role (leave blank to skip).</p>
                {preset.roles.map((r) => (
                  <div key={r.slug} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 170 }}>{r.name}</span>
                    <select className="sr-select" value={presetMembers[r.slug] || ""}
                      onChange={(e) => setPresetMembers((m) => ({ ...m, [r.slug]: e.target.value }))}
                      data-testid={`preset-role-${r.slug}`} style={{ minWidth: 180 }}>
                      <option value="">— Unassigned —</option>
                      {people.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
                    </select>
                  </div>
                ))}
                <div>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={applyPreset} disabled={applyingPreset} data-testid="apply-preset-btn">
                    {applyingPreset ? <Loader2 size={14} className="sr-spin" /> : <><Users size={14} /> Apply team</>}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
        {teamAssigns.length === 0 ? (
          <p className="sr-muted" style={{ fontSize: 13 }}>No scoped roles yet. Assign one above.</p>
        ) : (
          <div style={{ display: "grid", gap: 8 }} data-testid="team-assign-list">
            {teamAssigns.map((a) => (
              <div key={a.id} className="sr-card" data-testid={`team-assign-${a.id}`}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                <UserCog size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>
                    {a.user_name || a.user_email} — <span style={{ color: "var(--accent-strong)" }}>{a.role_name}</span>
                    {a.migrated && <span className="sr-badge sr-badge-gray" style={{ marginLeft: 8, fontSize: 10 }}>migrated</span>}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 12 }}>{a.user_email}</div>
                </div>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => revokeRole(a.id)} data-testid={`revoke-assign-${a.id}`}>Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
        <Metric icon={Megaphone} label="Active campaigns" value={stats.campaigns} />
        <Metric icon={CheckCircle2} label="Live" value={stats.live_campaigns} />
        <Metric icon={Sparkles} label="Growth plans" value={stats.growth_plans} />
        <Metric icon={Tag} label="Offers" value={stats.offers} />
      </div>

      <Section title="Campaigns" count={campaigns.length}>
        {campaigns.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No campaigns yet.</p> : (
          <div className="sr-card" style={{ overflowX: "auto" }}>
            <table className="sr-table" data-testid="detail-campaigns-table">
              <thead><tr><th>Name</th><th>Status</th><th>Budget/mo</th><th>Mode</th><th>Created</th></tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`detail-campaign-${c.id}`}>
                    <td style={{ textTransform: "capitalize" }}>{c.name}</td>
                    <td><span className={`sr-badge ${STATUS_BADGE[c.status] || "sr-badge-gray"}`}>{c.status}</span></td>
                    <td>{money(c.budget_monthly)}</td>
                    <td className="sr-muted" style={{ fontSize: 12 }}>{c.activation_mode === "API" ? "Automated" : "Managed"}</td>
                    <td className="sr-muted" style={{ fontSize: 12 }}>{fmt(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Growth plans" count={growth_plans.length}>
        {growth_plans.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No growth plans yet.</p> : (
          <div className="sr-card" style={{ overflowX: "auto" }}>
            <table className="sr-table" data-testid="detail-plans-table">
              <thead><tr><th>Version</th><th>Goal</th><th>Budget/mo</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {growth_plans.map((p) => (
                  <tr key={p.id} data-testid={`detail-plan-${p.id}`}>
                    <td>v{p.version}</td>
                    <td className="sr-muted" style={{ fontSize: 13 }}>{p.goal || "—"}</td>
                    <td>{money(p.monthly_budget)}</td>
                    <td><span className={`sr-badge ${STATUS_BADGE[p.status] || "sr-badge-gray"}`}>{p.status}</span></td>
                    <td className="sr-muted" style={{ fontSize: 12 }}>{fmt(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Offers" count={offers.length}>
        {offers.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No offers yet.</p> : (
          <div className="sr-card" style={{ overflowX: "auto" }}>
            <table className="sr-table" data-testid="detail-offers-table">
              <thead><tr><th>Name</th><th>Type</th><th>Discount</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={o.id} data-testid={`detail-offer-${o.id}`}>
                    <td>{o.name || o.headline || "—"}</td>
                    <td className="sr-muted" style={{ fontSize: 12 }}>{o.offer_type || "—"}</td>
                    <td className="sr-muted" style={{ fontSize: 13 }}>{o.discount_value != null ? `${o.discount_value}${o.discount_type === "percent" ? "%" : ""}` : "—"}</td>
                    <td><span className={`sr-badge ${STATUS_BADGE[o.status] || "sr-badge-gray"}`}>{o.status}</span></td>
                    <td className="sr-muted" style={{ fontSize: 12 }}>{fmt(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Change history" count={history.length}>
        {history.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No ownership or assignment changes recorded yet.</p> : (
          <div style={{ display: "grid", gap: 8 }} data-testid="history-timeline">
            {history.map((h) => {
              const meta = HISTORY_META[h.field] || { label: h.field, icon: Clock };
              return (
                <div key={h.id} className="sr-card" data-testid={`history-${h.id}`}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                  <meta.icon size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>
                      {meta.label}: <span className="sr-muted" style={{ fontWeight: 400 }}>{h.from_label || "—"}</span> → <b>{h.to_label || "—"}</b>
                    </div>
                    <div className="sr-muted" style={{ fontSize: 12 }}>by {h.changed_by_email || "system"} · {fmt(h.changed_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <div className="sr-card" style={{ marginTop: 20, border: "1px solid #E7B7B7", background: "#FDF3F3" }} data-testid="danger-zone">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <AlertTriangle size={16} style={{ color: "#B42318" }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: "#B42318" }}>Danger zone</div>
        </div>
        {data.tenant?.archived ? (
          <>
            <p className="sr-muted" style={{ fontSize: 13, marginBottom: 10 }}>This client is <b>archived</b> and hidden from the active clients list. Restore it to make it active again.</p>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={restoreClient} disabled={dangerBusy} data-testid="restore-client-btn">
              {dangerBusy ? <Loader2 size={13} className="sr-spin" /> : <RefreshCw size={13} />} Restore client
            </button>
          </>
        ) : (
          <p className="sr-muted" style={{ fontSize: 13, marginBottom: 10 }}>Archive hides this client from the active list but keeps all data (reversible). Permanent delete wipes the client and everything tied to them.</p>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {!data.tenant?.archived && (
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={archiveClient} disabled={dangerBusy} data-testid="archive-client-btn">
              {dangerBusy ? <Loader2 size={13} className="sr-spin" /> : <Archive size={13} />} Archive client
            </button>
          )}
          {!confirmDelete ? (
            <button className="sr-btn sr-btn-sm" style={{ background: "#B42318", color: "#fff" }} onClick={() => { setConfirmDelete(true); setConfirmName(""); }} disabled={dangerBusy} data-testid="delete-client-btn">
              <Trash2 size={13} /> Permanently delete
            </button>
          ) : null}
        </div>
        {confirmDelete && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: "1px solid #E7B7B7", background: "#fff" }} data-testid="delete-confirm">
            <p style={{ fontSize: 13, color: "var(--navy)", marginBottom: 8 }}>
              This permanently deletes <b>{data.tenant?.name}</b> and all their campaigns, offers, connections, and logs. This <b>cannot be undone</b>.
              Type the business name <b>{data.tenant?.name}</b> to confirm.
            </p>
            <input className="sr-input" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={data.tenant?.name} data-testid="delete-confirm-input" />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setConfirmDelete(false); setConfirmName(""); }} data-testid="delete-cancel-btn">Cancel</button>
              <button className="sr-btn sr-btn-sm" style={{ background: "#B42318", color: "#fff" }}
                onClick={deleteClient}
                disabled={dangerBusy || (confirmName || "").trim().toLowerCase() !== (data.tenant?.name || "").trim().toLowerCase()}
                data-testid="delete-confirm-btn">
                {dangerBusy ? <Loader2 size={13} className="sr-spin" /> : <Trash2 size={13} />} Delete permanently
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
